import React, { useMemo } from 'react';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import PropTypes from 'prop-types';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import OnboardingPlannerViewSectionHeader
  from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/Onboarding/Components/OnboardingPlannerViewSectionHeader';
import { useOnboardingPlannerViewSectionContext } from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/Onboarding/Sections/ViewSectionContext';
import AumInformationLayoutOverride
  from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/Onboarding/Sections/AumInformation/MetaOverride';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import InvestmentProfileFamilyMemberList
  from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/Onboarding/Sections/AumInformation/InvestmentProfileFamilyMemberList';
import { FDStack, FDTitleText } from '@facet-wealth/design';
import { getBorder } from 'Components/utils';
import InvestmentObjectiveTimeHorizons
  from 'Components/AdvisorApp/Viewport/MissionControl/Page/Versions/Default/Tabs/Onboarding/Sections/AumInformation/investmentObjectiveTimeHorizons';
import MetaControl from 'Components/UI/Meta/MetaControl';

const INVESTMENT_OBJECTIVE_ELEMENT_HEIGHT = 80;
const INVESTMENT_OBJECTIVE_ELEMENT_MAX_WIDTH = 400;

const styles = (theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(4),
    padding: theme.spacing(0, 3),
  },
  header: {
  },
  overview: {
    display: 'flex',
  },
  familyMembers: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
  },
  familyMember: {
    maxWidth: INVESTMENT_OBJECTIVE_ELEMENT_MAX_WIDTH,
    width: '100%',
    height: INVESTMENT_OBJECTIVE_ELEMENT_HEIGHT,
    marginRight: theme.spacing(2),
    background: theme.palette.common.white,
    ...getBorder(theme, { all: true }),
  },
});

function ViewSectionLayout({
  classes,
}) {
  const {
    resource,
  } = useMetaLayoutContext();

  const {
    onAddTask,
    onAddNote,
  } = useOnboardingPlannerViewSectionContext();

  const [sectionDataResource] = resource.attr('sectionData');

  const familyMembers = useMemo(() => sectionDataResource.attr('familyMembers')?.resources().list() || [], [sectionDataResource.attr('familyMembers')]);

  const AumInfoOverviewLayout = () => (
    <div className={classes.overview}>
      <div>
        <MetaField attr="prospectCurrentlyHasAum" />
      </div>
      <div>
        <MetaField attr="prospectPlansToInvest" />
        <MetaField attr="reasonProspectWillNotInvest" />
        <MetaField attr="otherReasonProspectWillNotInvest" />
      </div>
    </div>
  );

  return (
    <div className={classes.root}>
      <OnboardingPlannerViewSectionHeader
        className={classes.header}
        title={resource.attr('title')}
        onAddTask={onAddTask}
        onAddNote={onAddNote}
      />

      <div className={classes.body}>
        <div className={classes.overview}>
          <MetaFormInline
            className={classes.form}
            key={'aumInformationOverview'} // eslint-disable-line react/no-array-index-key
            resource={sectionDataResource}
            metaOverrideSet={AumInformationLayoutOverride}
            layout={AumInfoOverviewLayout}
            noIndividualSubmitButtons
            hideErrorList
            component={({ children }) => children}
          />
        </div>

        <FDStack direction="column" spacing={3} alignItems="flex-start">
          {familyMembers.length ? (
            <>
              <FDTitleText value="Investment profiles" color="textSecondary" />
              <div className={classes.familyMembers}>
                <InvestmentProfileFamilyMemberList
                  classes={{ familyMember: classes.familyMember }}
                  familyMembers={familyMembers}
                />
              </div>
            </>
          ) : null}
          <InvestmentObjectiveTimeHorizons />
          <MetaControl attrName="debtPayOffPreference" resource={sectionDataResource} metaOverrideSet={AumInformationLayoutOverride} />
        </FDStack>
      </div>
    </div>
  );
}

ViewSectionLayout.propTypes = {
  classes: PropTypes.object.isRequired,
};

ViewSectionLayout.defaultProps = {};

export default withStyles(styles)(ViewSectionLayout);
